/** biome-ignore-all lint/correctness/useUniqueElementIds: section navigation targets are shared intentionally */
"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import type { HomeProject } from "@/lib/projects";
import Bg_Cases_Flow from "~/public/assets/cases/cases-section-bg.png";
import { CaseCard } from "./case-card";
import { useCasesCardAnimation } from "./use-cases-card-animation";
export type CasesSectionProps = {
  projects: HomeProject[];
  tag?: string;
  title?: string;
  hasBackground?: boolean;
};
export default function CasesSection(t0) {
  const $ = _c(33);
  const {
    projects,
    tag: t1,
    title: t2,
    hasBackground: t3
  } = t0;
  const tag = t1 === undefined ? "Casos de Sucesso" : t1;
  const title = t2 === undefined ? "Projetos desenvolvidos para gerar impacto real." : t2;
  const hasBackground = t3 === undefined ? false : t3;
  const sectionRef = useRef(null);
  useCasesCardAnimation(sectionRef);
  if (projects.length === 0) {
    return null;
  }
  const t4 = `relative border-gray-300 text-white ${hasBackground ? "" : "border-t"}`;
  const t5 = `container relative z-10 overflow-visible md:border-x md:border-gray-300/80 pb-10 ${title ? "pt-24" : ""}`;
  let t6;
  let t7;
  let t8;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="max-xl:hidden inline-flex w-3 h-3 border border-gray-300 absolute -top-1.75 -left-1.75 bg-gray-300" />;
    t7 = <span className="max-xl:hidden inline-flex w-3 h-3 border border-gray-300 absolute -top-1.75 -right-1.75 bg-gray-300" />;
    t8 = <Image src={Bg_Cases_Flow} alt="" aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-50 h-auto w-full" priority={true} width={1920} height={1080} />;
    $[0] = t6;
    $[1] = t7;
    $[2] = t8;
  } else {
    t6 = $[0];
    t7 = $[1];
    t8 = $[2];
  }
  let t9;
  if ($[3] !== tag) {
    t9 = <p className="tag">{tag}</p>;
    $[3] = tag;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== title) {
    t10 = <h2 className="title max-w-110">{title}</h2>;
    $[5] = title;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== t10 || $[8] !== t9) {
    t11 = <div className="cases-section-heading">{t9}{t10}</div>;
    $[7] = t10;
    $[8] = t9;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== title) {
    t12 = title && <div className="flex items-center gap-8 max-sm:hidden"><button aria-label="Case anterior" className="cases-prev cases-section-control cursor-pointer transition-all duration-300 ease-in-out text-primary-300" type="button"><ArrowLeft className="size-12" strokeWidth={1.5} /></button><button aria-label={"Pr\xF3ximo case"} className="cases-next cases-section-control cursor-pointer transition-all duration-300 ease-in-out text-primary-300" type="button"><ArrowRight className="size-12" strokeWidth={1.5} /></button></div>;
    $[10] = title;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== t11 || $[13] !== t12) {
    t13 = <div className="flex flex-col gap-8 pb-10 sm:flex-row sm:items-end sm:justify-between px-6 lg:px-18 3xl:px-24! sm:pb-20">{t11}{t12}</div>;
    $[12] = t11;
    $[13] = t12;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      enabled: true
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [Navigation, A11y];
    t16 = {
      nextEl: ".cases-next",
      prevEl: ".cases-prev"
    };
    $[16] = t15;
    $[17] = t16;
  } else {
    t15 = $[16];
    t16 = $[17];
  }
  let t17;
  if ($[18] !== projects) {
    t17 = projects.map(_temp);
    $[18] = projects;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== t17) {
    t18 = <Swiper a11y={t14} className="overflow-visible!" modules={t15} navigation={t16} slidesPerView="auto" spaceBetween={16}>{t17}</Swiper>;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <button aria-label="Case anterior" className="cases-prev cases-section-control cursor-pointer transition-all duration-300 ease-in-out text-primary-300" type="button"><ArrowLeft className="size-9" strokeWidth={1.5} /></button>;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="mt-5 flex items-center gap-5 sm:hidden">{t19}<button aria-label={"Pr\xF3ximo case"} className="cases-next cases-section-control cursor-pointer transition-all duration-300 ease-in-out text-primary-300" type="button"><ArrowRight className="size-9" strokeWidth={1.5} /></button></div>;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== t18) {
    t21 = <div className="relative px-6 lg:px-18 3xl:px-24!">{t18}{t20}</div>;
    $[24] = t18;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== t13 || $[27] !== t21 || $[28] !== t5) {
    t22 = <div className="md:container-padding"><div className={t5}>{t6}{t7}{t8}{t13}{t21}</div></div>;
    $[26] = t13;
    $[27] = t21;
    $[28] = t5;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== t22 || $[31] !== t4) {
    t23 = <section ref={sectionRef} className={t4} id="cases">{t22}</section>;
    $[30] = t22;
    $[31] = t4;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  return t23;
}
function _temp(project) {
  return <SwiperSlide className="h-full w-full max-w-95" key={project.slug}><CaseCard project={project} /></SwiperSlide>;
}